import {useEffect, useSyncExternalStore} from 'react';
import {useRouteError, isRouteErrorResponse} from 'react-router-dom';

import {ErrorPage} from 'components/errors/ErrorPage';
import {useProductionErrorLogger} from 'components/errors/utils';
import {Loading} from 'components/loading/Loading';
import {useAppOverrides} from 'contexts/AppOverridesContext';
import {
  canReloadForChunkLoadError,
  getUnrecoveredChunkLoadError,
  isChunkLoadError,
  isChunkLoadReloadPending,
  reloadForChunkLoadError,
  subscribeToChunkLoadReloadPending,
} from 'staleChunkGuard';
import type {CourseId} from 'types/common';
import {useIsBrowserOffline} from 'utils/connectionMessages';

interface ErrorBoundaryProps {
  /**
   * The course ID to navigate to if the user is not signed in. If not provided,
   * will default to the current course ID. Only necessary for local development.
   */
  courseId?: CourseId;
}

interface ParsedError {
  message: string;
  data?: unknown;
}

function parseError(error: unknown): ParsedError {
  if (isRouteErrorResponse(error)) {
      return {
        message: `${error.status} ${error.statusText}`,
        data: error.data,
      }
  } else if (error != undefined && typeof error === 'object' && 'message' in error) {
      return {
        message: String(error.message),
        data: error,
      };
  } else if (typeof error === 'string') {
      return {
        message: String(error),
      };
  } else {
    return {
      message: 'Unknown error',
      data: error,
    };
  }
}

/**
 * A component that displays an error page when an error occurs. Should be used with
 * Suspense.
 */
const ErrorBoundary = ({courseId}: ErrorBoundaryProps) => {
  const error = useRouteError();
  const {logProductionErrorToFirestore} = useProductionErrorLogger();
  const {ErrorPage: OverrideErrorPage} = useAppOverrides();
  const isOffline = useIsBrowserOffline();
  // A pending reload also covers the secondary error thrown once the stale
  // chunk guard has made Vite resolve the failed import to `undefined`. React
  // caches a rejected lazy import, so a later navigation to that route lands
  // here without Vite reporting it again — hence the reload from here too.
  // Subscribed, not just read: the user can decline the reload, and this
  // must then give way to the error page.
  const isReloadPending = useSyncExternalStore(
    subscribeToChunkLoadReloadPending,
    isChunkLoadReloadPending,
  );
  // After a declined reload the secondary error is all that reaches here, so
  // report the failure the reload was for in its place.
  const chunkLoadError = isChunkLoadError(error)
    ? error
    : getUnrecoveredChunkLoadError();
  const isChunkError = chunkLoadError !== undefined;
  const isReloading =
    isReloadPending || (isChunkError && canReloadForChunkLoadError());

  useEffect(() => {
    if (isReloading) {
      reloadForChunkLoadError({error: chunkLoadError});
      return;
    }
    const reportedError = chunkLoadError ?? error;
    console.error('ErrorBoundary', parseError(reportedError));

    logProductionErrorToFirestore(reportedError as Error);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [error, isReloading]);

  if (isReloading) {
    return <Loading />;
  }

  const ErrorComponent = OverrideErrorPage ?? ErrorPage;
  if (isChunkError) {
    return (
      <ErrorComponent
        title={isOffline ? "You're offline" : "This page didn't finish loading"}
        msg={
          isOffline
            ? 'Reconnect to the internet, then reload the page.'
            : 'Check your connection, then reload the page.'
        }
        courseId={courseId}
        showReload={true}
      />
    );
  }
  return (
    <ErrorComponent
      title="Something went wrong"
      msg="A log has been sent to the course staff"
      courseId={courseId}
    />
  );
};

export default ErrorBoundary;
